/**
 * Authentication Guard
 */

import type { RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores/auth.store'

let initialRedirectDone = false

export function resetInitialRedirect() {
    initialRedirectDone = false
}

/**
 * Resolves the initial landing route on login or initial application load:
 * 1. "Save current page as default" (overrides EasyNav)
 * 2. EasyNav mode (if enabled):
 *    a. Last visited page before closing (mondai-last-visited-url)
 *    b. Saved persona hub (/easy-nav/:persona)
 *    c. Choose path (/easy-nav)
 * 3. Default fallback: Dashboard ('/')
 */
export function getInitialLandingRoute(): string {
    // 1. "Save current page as default" overrides everything if set
    const savedDefault = localStorage.getItem('mondai-default-opening-url')
    if (savedDefault && savedDefault !== '/' && !savedDefault.startsWith('/auth')) {
        return savedDefault
    }

    // 2. EasyNav mode (if enabled)
    const authStore = useAuthStore()
    if (authStore.easyNavPrefs.enabled) {
        // A. Last page visited before closing
        const lastVisited = localStorage.getItem('mondai-last-visited-url')
        if (
            lastVisited &&
            lastVisited !== '/' &&
            !lastVisited.startsWith('/auth') &&
            !lastVisited.startsWith('/onboarding')
        ) {
            return lastVisited
        }

        // B. Saved persona path (e.g. /easy-nav/partner)
        const savedPersona = authStore.easyNavPrefs.persona
        if (savedPersona) {
            return `/easy-nav/${savedPersona}`
        }

        // C. Choose path overview
        return '/easy-nav'
    }

    // 3. Fallback: Dashboard
    return '/'
}

export async function authGuard(
    to: RouteLocationNormalized,
    _from: RouteLocationNormalized
) {
    const authStore = useAuthStore()

    // 1. Wait for auth to initialize if still loading (first boot)
    if (authStore.isLoading) {
        await new Promise<void>((resolve) => {
            const unwatch = authStore.$subscribe((_mutation, state) => {
                if (!state.isLoading) {
                    unwatch()
                    resolve()
                }
            })
            // Aggressive 2s timeout on boot to ensure the UI renders
            setTimeout(() => {
                unwatch()
                resolve()
            }, 2000)
        })
    }

    const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
    const requiresGuest = to.matched.some((record) => record.meta.requiresGuest)
    const isPublic = to.matched.some((record) => record.meta.public)

    // Allow public routes immediately
    if (isPublic) return true

    // 2. Perform standard auth checks
    if (requiresAuth && !authStore.isAuthenticated) {
        // Redirect to login with return URL
        return {
            name: 'login',
            query: { redirect: to.fullPath },
        }
    } else if (requiresGuest && authStore.isAuthenticated) {
        // Already logged in, redirect to initial landing page
        const target = getInitialLandingRoute()
        initialRedirectDone = true
        if (target && target !== '/') {
            return target
        }
        return { name: 'home' }
    } else if (
        authStore.isAuthenticated &&
        authStore.needsOnboarding &&
        to.name !== 'onboarding' &&
        to.name !== 'logout' &&
        to.name !== 'reset-password'
    ) {
        // Redirect to onboarding if not completed
        return { name: 'onboarding' }
    } else if (authStore.profile?.status === 'suspended') {
        // Block access for suspended users
        authStore.signOut()
        return { name: 'login', query: { error: 'account_suspended' } }
    }

    // 3. EasyNav welcome redirect — send new users to the guided landing page
    //    once, right after they complete onboarding (seen_welcome = false).
    //    Skip if already heading to an easy-nav route to avoid redirect loops.
    const isEasyNavRoute = String(to.name ?? '').startsWith('easy-nav')
    if (
        authStore.isAuthenticated &&
        !authStore.needsOnboarding &&
        !isEasyNavRoute &&
        authStore.easyNavPrefs.enabled &&
        !authStore.easyNavPrefs.seen_welcome
    ) {
        return { name: 'easy-nav' }
    }

    // 4. Initial landing redirect when accessing root '/'
    if (authStore.isAuthenticated && !authStore.needsOnboarding && to.path === '/') {
        if (!initialRedirectDone) {
            initialRedirectDone = true
            const target = getInitialLandingRoute()
            if (target && target !== '/') {
                return target
            }
        }
    }

    // Mark as done only for authenticated navigations
    if (authStore.isAuthenticated) {
        initialRedirectDone = true
    }

    // Allow navigation
    return true
}
